import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { DateTime } from "luxon"
import { GetLocationResultErr, GetLocationResultOk } from "../lib/location"
import { AppRootState } from "./appStore"
import { LegacyLocationPermission, LegacyLocationQuery, Location } from "@fusion-advantage/backcompat"
import { LocationStatus } from "@fusion-advantage/formrender"
import { RootRouteFormContent } from "@fusion-advantage/formlib"

// hold the last-known-good location for 15 minutes after a failed refresh
// before reverting to null
export const LOCATION_HOLD_MS = 15 * 60 * 1000

export interface LocationSliceState {
	querying: boolean
	result: GetLocationResultOk|GetLocationResultErr|null
	updatedAt: number|null
	// last successful fix, kept separate from `result` so a failed refresh
	// does not clobber it; `result` still tracks the live poll outcome
	lastSuccess: { location: Location, at: number }|null
	nextModalSkipContent: RootRouteFormContent|null
}

const initialState: LocationSliceState = {
	querying: false,
	result: null,
	updatedAt: null,
	lastSuccess: null,
	nextModalSkipContent: null,
}

const locationSlice = createSlice({
	name: 'location',
	initialState,
	reducers: {
		setLocationQuerying(state) {
			state.querying = true
		},
		setLocationResult(state, action: PayloadAction<GetLocationResultOk|GetLocationResultErr>) {
			const now = DateTime.now().toMillis()
			state.querying = false
			state.result = action.payload
			state.updatedAt = now
			// only a successful fix updates the last-known-good location;
			// errors leave the previous good value in place for the hold period
			if(action.payload.status === LocationStatus.Success) {
				state.lastSuccess = { location: action.payload.location, at: now }
			}
		},
		setLocationNextModalSkipContent(state, action: PayloadAction<RootRouteFormContent|null>) {
			state.nextModalSkipContent = action.payload ? {
				...action.payload,
				skipLocationCheck: true,
			} : null
		},
	},
})

export const { setLocationQuerying, setLocationResult, setLocationNextModalSkipContent } = locationSlice.actions
export default locationSlice.reducer

export const selectLocationStatus = createSelector(
	(state: AppRootState) => state.location.querying,
	(state: AppRootState) => state.location.result,
	(state: AppRootState) => state.location.lastSuccess,
	(querying, result, lastSuccess): LocationStatus|null => {
		if(querying) {
			// still "refreshing" (not "querying from scratch") if we can
			// currently supply a previously-acquired location
			if(lastSuccess) {
				return LocationStatus.Refreshing
			}
			return LocationStatus.Querying
		}
		if(!result) {
			return null
		}
		return result.status
	}
)

// provide user location in a common format for Dan's procedures
// null if unable to be acquired, or if the last-known-good fix is older than
// the hold window.
// NB: plain function (not createSelector) so the age check re-evaluates on
// every call; this is only ever read imperatively via getState(), and
// memoization would prevent the value from expiring on time alone.
export const selectImportableLocation = (state: AppRootState): string|null => {
	const ls = state.location.lastSuccess
	if(!ls) {
		return null
	}
	if(DateTime.now().toMillis() - ls.at >= LOCATION_HOLD_MS) {
		return null
	}
	return `${ls.location.latitude},${ls.location.longitude}`
}

export const selectLegacyLocationQuery = (state: AppRootState): LegacyLocationQuery => {
	const ls = state.location.lastSuccess
	if(ls && DateTime.now().toMillis() - ls.at < LOCATION_HOLD_MS) {
		return {
			permission: LegacyLocationPermission.Granted,
			location: ls.location,
			updatedAt: ls.at,
		}
	}
	return {
		permission: LegacyLocationPermission.Denied,
		updatedAt: state.location.updatedAt ?? DateTime.now().toMillis(),
	}
}
